// Copyright 2023, mochabug AB
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { fromJsonString, toJson } from '@bufbuild/protobuf';
import { createValidator } from '@bufbuild/protovalidate';
import chalk from 'chalk';
import fg from 'fast-glob';
import fs from 'fs';
import { isSchema, isValidSchema } from 'jtd';
import { mkdirp } from 'mkdirp';
import path from 'path';
import { fileURLToPath } from 'url';
import { JTDSchemaSchema } from './genproto/mochabugapis/adapt/graph/v1/jtd_schema_pb';
import { SignalFormat } from './genproto/mochabugapis/adapt/graph/v1/signal_format_pb';
import {
  VertexMetadata,
  VertexMetadataSchema
} from './genproto/mochabugapis/adapt/graph/v1/vertex_metadata_pb';
import {
  Manifest,
  ManifestSchema
} from './genproto/mochabugapis/adapt/plugins/v1/manifest_pb';
import { Oauth2GrantType } from './genproto/mochabugapis/adapt/plugins/v1/oauth2_service_pb';
import { ServiceDefinition } from './genproto/mochabugapis/adapt/plugins/v1/service_definition_pb';
import { VertexSchema } from './genproto/mochabugapis/adapt/plugins/v1/vertex_pb';

const validator = createValidator();

export function readAsset(name: string): string {
  return fs
    .readFileSync(
      path.join(
        path.dirname(fileURLToPath(import.meta.url)),
        '..',
        'assets',
        name
      )
    )
    .toString();
}

export function writeFile(filePath: string, content: string) {
  const dir = path.dirname(filePath);
  mkdirp.sync(dir);
  fs.writeFileSync(filePath, content);
}

export async function readManifest(
  filePath: string,
  src: string
): Promise<Manifest | null> {
  try {
    const baseDir = path.dirname(filePath);
    if (!fs.existsSync(filePath)) {
      throw new Error(
        `Invalid command: Manifest file not found at ${filePath}.`
      );
    }

    const manifestData = fs.readFileSync(filePath, 'utf-8');
    const manifest = fromJsonString(ManifestSchema, manifestData);

    const verticesPath = path.join(baseDir, src);
    if (!fs.existsSync(verticesPath)) {
      throw new Error(`Directory does not exist: ${verticesPath}`);
    }
    if (!fs.lstatSync(verticesPath).isDirectory()) {
      throw new Error(`Path exists but is not a directory: ${verticesPath}`);
    }

    const vertexFiles = fg
      .sync('**/vertex.json', {
        cwd: verticesPath,
        absolute: false
      })
      .map((f) => path.join(verticesPath, f));
    if (vertexFiles.length === 0) {
      throw new Error(
        `No vertex files found in the directory: ${verticesPath}. Please ensure that the directory contains at least one vertex file.`
      );
    }

    // Load and adjust the vertex files.
    manifest.vertices = vertexFiles.map((vertexFile) => {
      const vertex = fromJsonString(
        VertexSchema,
        fs.readFileSync(vertexFile, 'utf-8')
      );

      // Adjust metadata.json path.
      vertex.metadata = vertex.metadata
        ? path.join(path.dirname(vertexFile), vertex.metadata)
        : path.join(path.dirname(vertexFile), 'metadata.json');

      // Adjust paths for config and logo if they exist.
      if (vertex.config) {
        vertex.config = path.join(path.dirname(vertexFile), vertex.config);
      }
      if (vertex.logo) {
        vertex.logo = path.join(path.dirname(vertexFile), vertex.logo);
      }
      return vertex;
    });

    // Validate service references in each vertex.
    for (const v of manifest.vertices) {
      for (const serviceName of v.userServices) {
        if (!manifest.serviceDefinitions.find((s) => s.name === serviceName)) {
          throw new Error(
            `Service not found: ${serviceName} in vertex: ${v.name}`
          );
        }
      }
    }

    // Validate the system service definition references
    for (const serviceName of manifest.systemServices) {
      if (!manifest.serviceDefinitions.find((s) => s.name === serviceName)) {
        throw new Error(`Service not found: ${serviceName} in system services`);
      }
    }

    // Validate the vertex metadata schemas.
    for (const v of manifest.vertices) {
      const vertexMetadata = fromJsonString(
        VertexMetadataSchema,
        fs.readFileSync(v.metadata, 'utf-8')
      );
      await validateVertexConfigMetadata(
        vertexMetadata,
        `Vertex file: ${v.metadata}`
      );
    }

    // Make sure that code flow is configured correctly
    validateOauthCodeFlow(manifest);

    // Validate default values in service definitions.
    for (const srv of manifest.serviceDefinitions) {
      await validateLoadServiceDefinition(baseDir, srv);
    }

    const result = validator.validate(ManifestSchema, manifest);
    if (result.kind != 'valid') {
      console.error(result.error);
      console.error(result.violations);
      throw new Error(`Manifest validation failed: ${result.error}`);
    }

    return manifest;
  } catch (error: any) {
    console.error(
      chalk.bgRed.white.bold('\n FATAL ERROR: Manifest loading failed! \n')
    );
    console.error(chalk.redBright(error.message));
    process.exit(1);
  }
}

export function labelize(val: string): string {
  return (val.charAt(0).toUpperCase() + val.substring(1)).replace('_', ' ');
}

export function validateOauthCodeFlow(manifest: Manifest) {
  const codeDefs: ServiceDefinition[] = [];
  for (const srv of manifest.serviceDefinitions) {
    if (
      srv.type.case !== 'oauth2' ||
      srv.type.value.grantType !== Oauth2GrantType.AUTHORIZATION_CODE
    ) {
      continue;
    }
    codeDefs.push(srv);
  }

  // Now, make sure that all of the references in manifest.systemServices to any of the codeDefs are non-optional
  const codeDefNames = codeDefs.map((s) => s.name);
  for (const serviceName of manifest.systemServices) {
    if (codeDefNames.includes(serviceName)) {
      const serviceDef = manifest.serviceDefinitions.find(
        (s) => s.name === serviceName
      );
      if (serviceDef?.optional) {
        throw new Error(
          `Service ${serviceName} is using the OAuth2 code flow, it must be non-optional in the system services`
        );
      }
    }
  }

  // Now, we need to validate that if a codeDef is used in any vertex, it must also exist in the systemServices
  for (const vertex of manifest.vertices) {
    for (const serviceName of vertex.userServices) {
      if (codeDefNames.includes(serviceName)) {
        if (!manifest.systemServices.includes(serviceName)) {
          throw new Error(
            `Service ${serviceName} is using the OAuth2 code flow, it must be in the system services`
          );
        }
      }
    }
  }
}

// Service definitions can have files, these needs to be loaded and validated
export async function validateLoadServiceDefinition(
  basePath: string,
  srv: ServiceDefinition
) {
  switch (srv.type.case) {
    case 'variable': {
      // Validate the JTD schema.
      try {
        const schema = toJson(JTDSchemaSchema, srv.type.value.schema!);
        if (!isSchema(schema) || !isValidSchema(schema)) {
          throw new Error(`Invalid JTD schema provided.`);
        }

      } catch (error: any) {
        throw new Error(
          `Validation failed for service definition '${
            srv.name
          }': ${formatErrorMessage(error.message)}`
        );
      }
      break;
    }
  }
}

/**
 * Formats an error message by splitting it on commas and indenting each part.
 */
function formatErrorMessage(message: string): string {
  return message
    .split(/,\s*/g)
    .map((line) => `  - ${line}`)
    .join('\n');
}

export async function validateVertexConfigMetadata(
  metadata: VertexMetadata,
  context: string = 'Vertex'
) {
  // We need to run the validator here as well
  const result = validator.validate(VertexMetadataSchema, metadata);
  if (result.kind != 'valid') {
    throw new Error(
      `Vertex metadata validation failed: ${result.error}\n${formatErrorMessage(
        JSON.stringify(result.violations)
      )}`
    );
  }

  // Validate the exchanges
  for (let exch of metadata.exchanges) {
    for (let transmitter of exch.transmitters) {
      try {
        await validateFormats(
          transmitter.signals.map((s) => s.formats).flat(),
          `${context} > Procedure ${exch.name} > Transmitter ${transmitter.name}`
        );
      } catch (err: any) {
        throw new Error(
          `Error validating signals in ${context} > Procedure ${
            exch.name
          } > Transmitter ${transmitter.name}:\n${formatErrorMessage(
            err.message
          )}`
        );
      }
    }
    for (let receiver of exch.receivers) {
      try {
        await validateFormats(
          receiver.signals.map((s) => s.formats).flat(),
          `${context} > Procedure ${exch.name} > Receiver ${receiver.name}`
        );
      } catch (err: any) {
        throw new Error(
          `Error validating signals in ${context} > Procedure ${
            exch.name
          } > Receiver ${receiver.name}:\n${formatErrorMessage(err.message)}`
        );
      }
    }
  }

  // Validate top-level transmitters
  for (let transmitter of metadata.transmitters) {
    try {
      await validateFormats(
        transmitter.signals.map((s) => s.formats).flat(),
        `${context} > Top-level Transmitter ${transmitter.name}`
      );
    } catch (err: any) {
      throw new Error(
        `Error validating signals in ${context} > Top-level Transmitter ${
          transmitter.name
        }:\n${formatErrorMessage(err.message)}`
      );
    }
  }

  // We need to check the JTD schemas here since other formats are checked by the protovalidate
  for (let receiver of metadata.receivers) {
    try {
      await validateFormats(
        receiver.bindings.map((b) => b.accepts).flat(),
        `${context} > Top-level Receiver ${receiver.name}`
      );
    } catch (err: any) {
      throw new Error(
        `Error validating signals in ${context} > Top-level Receiver ${
          receiver.name
        }:\n${formatErrorMessage(err.message)}`
      );
    }
  }
}

export async function validateFormats(
  formats: SignalFormat[],
  context: string = 'Signal'
) {
  for (const format of formats)
    if (format.format.case === 'jtdSchema') {
      const schema = toJson(JTDSchemaSchema, format.format.value);
      if (!isSchema(schema) || !isValidSchema(schema)) {
        throw new Error(`Invalid JTD schema provided: ${context}`);
      }
    }
}
